import { useState, useEffect } from 'react';
import { tecDocToast } from '../../utils/toast';
import { TecSprite } from '../shared/TecSprite';
import { useAPIService } from '../../services/apiService';
import './SlashPhrasesModal.css';

interface SlashPhrase {
  id: number;
  label: string;
  auto_text: string | null;
  system: boolean;
  created_by_id: number;
}

interface SlashPhrasesModalProps {
  isOpen: boolean;
  onClose: () => void;
  globalContext: any;
}

function SlashPhrasesModal({ isOpen, onClose, globalContext }: SlashPhrasesModalProps) {
  const [slashPhrases, setSlashPhrases] = useState<SlashPhrase[]>([]);
  const [selectedPhraseId, setSelectedPhraseId] = useState<number | null>(null);
  const [currentPhrase, setCurrentPhrase] = useState<SlashPhrase | null>(null);
  const [addSlashPhrase, setAddSlashPhrase] = useState<string>('');
  const [isSuperuser, setIsSuperuser] = useState<boolean>(false);
  
  const apiService = useAPIService();

  useEffect(() => {
    if (isOpen && globalContext?.loggedInEmployee) {
      // Initialize data from global context
      const phrases = globalContext.loggedInEmployee.slash_phrases || [];
      setSlashPhrases([...phrases].sort((a, b) => a.label.localeCompare(b.label)));
      setIsSuperuser(globalContext.loggedInEmployee.is_superuser || false);
    }
  }, [isOpen, globalContext]);

  const changePhrase = (selectedPhraseId: number) => {
    if (selectedPhraseId) {
      const phrase = slashPhrases.find(p => p.id === selectedPhraseId);
      setCurrentPhrase(phrase || null);
      setSelectedPhraseId(selectedPhraseId);
    }
  };

  const addPhrase = async () => {
    if (!addSlashPhrase.trim()) return;

    const newPhrase = {
      label: addSlashPhrase.trim(),
      created_by_id: globalContext.loggedInEmployee.id,
      auto_text: null
    };

    try {
      const response = await apiService.post('/account/add_slash_phrase', 
        { phrase: newPhrase }, 
        'Adding slash phrase...'
      );
      
      const addedPhrase = response.data.phrase;
      const updatedPhrases = [...slashPhrases, addedPhrase].sort((a, b) => a.label.localeCompare(b.label));
      setSlashPhrases(updatedPhrases);
      setAddSlashPhrase('');
      setCurrentPhrase(null);
      
    } catch (error) {
      console.error('Error adding phrase:', error);
      tecDocToast.error('Failed to add slash phrase');
    }
  };

  const savePhrase = async () => {
    if (!currentPhrase) return;

    try {
      await apiService.post('/account/save_slash_phrase', 
        { phrase: currentPhrase }, 
        'Saving slash phrase...'
      );

      tecDocToast.success(`${currentPhrase.label} phrase saved.`);
      
    } catch (error) {
      console.error('Error saving phrase:', error);
      tecDocToast.error('Failed to save slash phrase');
    }
  };

  const deletePhrase = async () => {
    if (!currentPhrase) return;

    try {
      await apiService.post('/account/delete_slash_phrase', 
        { phrase: currentPhrase }, 
        'Deleting slash phrase...'
      );

      const updatedPhrases = slashPhrases.filter(s => s.id !== currentPhrase.id);
      setSlashPhrases(updatedPhrases);
      setCurrentPhrase(null);
      setSelectedPhraseId(null);
      
      tecDocToast.success(`${currentPhrase.label} phrase deleted.`);
      
    } catch (error) {
      console.error('Error deleting phrase:', error);
      tecDocToast.error('Failed to delete slash phrase');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="slash-modal-overlay" onClick={onClose}>
      <div className="slash-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="slash-modal-header">
          <h2>Slash Phrases</h2>
          <button className="slash-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="slash-container">
          <div className="slash-left-column">
            <div>
              <h3 className="slash-header">Slash Phrases</h3>
              <select 
                size={22} 
                onChange={(e) => changePhrase(parseInt(e.target.value))}
                value={selectedPhraseId || ''}
                className="slash-select-box"
              >
                <option value="">Select a phrase...</option>
                {slashPhrases.map((phrase) => (
                  <option key={phrase.id} value={phrase.id}>
                    {phrase.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="slash-footer">
              <input 
                type="text" 
                value={addSlashPhrase}
                onChange={(e) => setAddSlashPhrase(e.target.value)}
                className="slash-text-input"
                placeholder="Enter new phrase name..."
              />
              <button 
                onClick={addPhrase} 
                className="tec-button-square-md tec-button-red add"
              >
                <TecSprite icon="plus" color="white" size="md" />
              </button>
            </div>
          </div>
          
          <div className="slash-right-column">
            <div>
              <h3 className="slash-header">
                Auto Text
                {currentPhrase?.system && (
                  <span> (System Command - Editable by Admin)</span>
                )}
              </h3>
              <textarea 
                disabled={currentPhrase?.system && !isSuperuser}
                value={currentPhrase?.auto_text || ''}
                onChange={(e) => setCurrentPhrase(prev => prev ? {...prev, auto_text: e.target.value} : null)}
                className="slash-editor-textarea"
                placeholder={currentPhrase ? "Enter auto text for this phrase..." : "Select a phrase to edit"}
              />
            </div>
            <div className="slash-footer">
              <button 
                disabled={!currentPhrase || (currentPhrase.system && !isSuperuser)}
                onClick={savePhrase}
                className="tec-button-md tec-button-red" 
                type="button"
              >
                Save
              </button>
              <button 
                disabled={!currentPhrase || (currentPhrase.system && !isSuperuser)}
                onClick={deletePhrase}
                className="tec-button-md tec-button-red" 
                type="button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SlashPhrasesModal;